import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../common/Modal";
import Form from "../../common/Form";
import Button from "../../common/Button";
import thumbnailPlaceholder from "../../../assets/images/thumbnail-placeholder.png";
import { formatDate } from "../../../utils/manipulateText";
import { renderTemplateSlideItemPreview } from "../templates";

const NewsItemModal = ({
  isVisible,
  onClose,
  news,
  styleSettings = {},
  selectedVideoTemplate,
  videoRestrictions = {},
  isReadonly = false,
  onSave,
  loading = false,
}) => {
  const { t } = useTranslation();

  const [formState, setFormState] = useState(news || {});

  useEffect(() => {
    if (isVisible) {
      setFormState(news || {});
    }
  }, [news, isVisible]);

  const isTitleAvailable = !!videoRestrictions["items[].title"];
  const isTitleValid = isTitleAvailable
    ? formState?.title &&
      formState?.title?.length <= videoRestrictions["items[].title"]?.maxLength
    : true;

  const isSubTitleAvailable = !!videoRestrictions["items[].subTitle"];
  const isSubTitleValid = isSubTitleAvailable
    ? formState?.subTitle &&
      formState?.subTitle?.length <=
        videoRestrictions["items[].subTitle"]?.maxLength
    : true;

  const isVoiceTextAvailable = !!videoRestrictions["items[].voiceText"];
  const isVoiceTextValid = isVoiceTextAvailable
    ? formState?.voiceText &&
      formState?.voiceText?.length <=
        videoRestrictions["items[].voiceText"]?.maxLength
    : true;

  const isCategoryAvailable = !!videoRestrictions["items[].category"];
  const isCategoryValid = isCategoryAvailable
    ? formState?.category &&
      formState?.category?.length <=
        videoRestrictions["items[].category"]?.maxLength
    : true;

  const isImageAvailable = !!videoRestrictions["items[].image"];
  const isImageValid = isImageAvailable ? formState?.image : true;

  const isValid =
    isTitleValid &&
    isSubTitleValid &&
    isVoiceTextValid &&
    isImageValid &&
    isCategoryValid;

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setFormState((prev) => ({ ...prev, image: event.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    if (!isValid || isReadonly) return;
    onSave?.(formState);
    onClose?.();
  };

  return (
    <Modal
      isVisible={isVisible}
      onClose={onClose}
      title={t("edit news")}
      loading={loading}
      customClass="max-w-6xl w-full"
    >
      <div className="flex flex-row gap-4">
        <div className="mx-5 my-3 w-full">
          {formState?.newsDate && (
            <div className="text-md text-gray-700 mb-4">
              <b>{t("news date")}:</b> {formatDate(formState.newsDate)}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            {isImageAvailable && (
              <div className="md:col-span-1">
                <div className="w-full grid grid-cols-1 gap-2">
                  <div
                    className={`relative w-full aspect-video bg-gray-100 rounded overflow-hidden ${
                      !isImageValid ? "border-2 border-red-500" : ""
                    }`}
                  >
                    {/* image preview */}
                    <img
                      src={formState?.image}
                      alt={t("news thumbnail")}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = thumbnailPlaceholder;
                      }}
                    />
                    <input
                      readOnly={isReadonly}
                      type="file"
                      accept="image/*"
                      className={`absolute inset-0 w-full h-full opacity-0 cursor-pointer ${
                        isReadonly ? "pointer-events-none" : ""
                      }`}
                      disabled={isReadonly}
                      onChange={handleImageChange}
                      title={t("click to replace image")}
                    />
                    <div className="absolute bottom-0 right-0 bg-blue-500 text-white text-xs px-1 rounded-tl">
                      📷
                    </div>
                  </div>
                  {!isImageValid && (
                    <div className="text-xs text-red-500 -mt-2 mb-2">
                      {t("image is required")}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div
              className={`${
                isImageAvailable ? "md:col-span-2" : "md:col-span-3"
              }`}
            >
              <Form>
                {isTitleAvailable && (
                  <>
                    <Form.FormTextInput
                      label={t("news title")}
                      name={`title-${formState?.id}`}
                      value={formState?.title || ""}
                      onChange={(e) =>
                        setFormState((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      maxLength={videoRestrictions["items[].title"]?.maxLength}
                      readOnly={isReadonly}
                      className="text-sm font-semibold bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                      placeholder={t("news title")}
                      error={
                        !formState?.title?.length
                          ? t("title is required")
                          : formState?.title?.length >
                            videoRestrictions["items[].title"]?.maxLength
                          ? t("title is too long")
                          : ""
                      }
                    />
                    <div className="text-xs text-gray-500 -mt-2 mb-2">
                      {(formState?.title?.length || 0) +
                        " / " +
                        (videoRestrictions["items[].title"]?.maxLength || 0)}
                    </div>
                  </>
                )}

                {isSubTitleAvailable && (
                  <>
                    <Form.FormTextInput
                      label={t("sub title")}
                      name={`subTitle-${formState?.id}`}
                      readOnly={isReadonly}
                      value={formState?.subTitle || ""}
                      onChange={(e) =>
                        setFormState((prev) => ({
                          ...prev,
                          subTitle: e.target.value,
                        }))
                      }
                      maxLength={
                        videoRestrictions["items[].subTitle"]?.maxLength
                      }
                      className="text-xs bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                      placeholder={t("sub title")}
                      error={
                        !formState?.subTitle?.length
                          ? t("sub title is required")
                          : formState?.subTitle?.length >
                            videoRestrictions["items[].subTitle"]?.maxLength
                          ? t("sub title is too long")
                          : ""
                      }
                    />
                    <div className="text-xs text-gray-500 -mt-2 mb-2">
                      {(formState?.subTitle?.length || 0) +
                        " / " +
                        (videoRestrictions["items[].subTitle"]?.maxLength || 0)}
                    </div>
                  </>
                )}

                {isCategoryAvailable && (
                  <>
                    <Form.FormTextInput
                      label={t("category")}
                      name={`category-${formState?.id}`}
                      value={formState?.category || ""}
                      onChange={(e) =>
                        setFormState((prev) => ({
                          ...prev,
                          category: e.target.value,
                        }))
                      }
                      maxLength={
                        videoRestrictions["items[].category"]?.maxLength
                      }
                      readOnly={isReadonly}
                      className="text-xs bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
                      placeholder={t("category")}
                      error={
                        !formState?.category?.length
                          ? t("category is required")
                          : formState?.category?.length >
                            videoRestrictions["items[].category"]?.maxLength
                          ? t("category is too long")
                          : ""
                      }
                    />
                    <div className="text-xs text-gray-500 -mt-2 mb-2">
                      {(formState?.category?.length || 0) +
                        " / " +
                        (videoRestrictions["items[].category"]?.maxLength || 0)}
                    </div>
                  </>
                )}

                {isVoiceTextAvailable && (
                  <>
                    <Form.Textarea
                      label={t("voice text")}
                      name={`voiceText-${formState?.id}`}
                      value={formState?.voiceText || ""}
                      readOnly={isReadonly}
                      onChange={(e) =>
                        setFormState((prev) => ({
                          ...prev,
                          voiceText: e.target.value,
                        }))
                      }
                      className="text-xs text-gray-700 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1 resize-none"
                      placeholder={t("voice text")}
                      tooltipTitle={t("voice text placement")}
                      tooltipContent={
                        <div style={{ width: 300 }}>
                          {t(
                            "voice text will be converted to speech and used as voiceover narration in your video content"
                          )}
                        </div>
                      }
                      error={
                        !formState?.voiceText?.length
                          ? t("voice text is required")
                          : formState?.voiceText?.length >
                            videoRestrictions["items[].voiceText"]?.maxLength
                          ? t("voice text is too long")
                          : ""
                      }
                    />
                    <div className="text-xs text-gray-600 -mt-2">
                      {formState?.voiceText?.length || 0} /{" "}
                      {videoRestrictions["items[].voiceText"]?.maxLength} (
                      {t("estimated video duration")}:{" "}
                      {Number.isFinite(formState?.voiceText?.length)
                        ? Math.ceil(formState.voiceText.length / 13.5).toFixed(
                            2
                          )
                        : "0.00"}
                      s)
                    </div>
                  </>
                )}
              </Form>
            </div>
            <div className="md:col-span-3">
              {renderTemplateSlideItemPreview({
                template: selectedVideoTemplate?.name,
                news: formState,
                styleSettings: styleSettings,
              })}
            </div>
          </div>

          <div className="flex flex-row justify-end items-center w-full mt-4 gap-2">
            <Button color="secondary" type="button" onClick={onClose}>
              {t("cancel")}
            </Button>
            <Button
              color="inverse_primary"
              type="button"
              onClick={handleSave}
              disabled={!isValid || isReadonly}
              loading={loading}
            >
              {t("save")}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default NewsItemModal;
